// import loadable from '@loadable/component';
import { PropsWithChildren, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useMobile } from "@krea/customer-features/hooks";
// import ImportantEuLoanIcon from '@krea/customer-features/images/svg/important-info/eu-loan-flag.svg';
import { AccordionItem } from "@krea/customer-features/shared-components/accordion/AccordionItem";
import BidBenefits from "@krea/customer-features/shared-components/bid-dashboards/commons/BidBenefits";
import LenderResponseFieldRequestDetails from "@krea/customer-features/shared-components/bid-dashboards/commons/LenderResponseFieldRequestDetails";
import { Text } from "@krea/customer-features/shared-components/text";
import { ILenderCompany } from "@krea/customer-features/types/lenderCompany";
import { ILenderResponse } from "@krea/customer-features/types/lenderResponse";
import { FIELD_REQUEST_TYPES } from "@krea/customer-features/utils";
import twMerge from "@krea/customer-features/utils/tailwind-utils";

const DetailsLabel = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => {
  return (
    <Text
      size="md"
      className={twMerge("tw-text-gray-600 tw-font-bold tw-mb-2", className)}
    >
      {children}
    </Text>
  );
};

interface IBidsListItemDetails {
  details: ReactNode;
  lenderResponse: ILenderResponse;
  lenderCompany: ILenderCompany;
}

const BidsListItemDetails = ({
  details,
  lenderResponse,
  lenderCompany,
}: IBidsListItemDetails) => {
  const { t } = useTranslation();
  const isMobile = useMobile();

  const collateralFieldRequests = lenderResponse.fieldRequests.filter(
    ({ type }) => type === FIELD_REQUEST_TYPES.COLLATERAL,
  );

  const additionalInformationFieldRequests =
    lenderResponse.fieldRequests.filter(
      ({ type }) => type === FIELD_REQUEST_TYPES.ADDITIONAL_INFORMATION,
    );

  const isPaidOut = !!lenderResponse.paidOutAt;

  return (
    <div
      className={twMerge("tw-bg-white tw-mt-4 tw-pb-[3px]", {
        "tw-mx-1": !isMobile,
        "tw--mx-1": isMobile,
        "tw-border-mint-500 tw-border tw-border-solid tw-rounded-md": isPaidOut,
      })}
    >
      <div
        className={twMerge({
          "tw-w-full tw-flex tw-justify-between": !isMobile,
        })}
      >
        <div className="tw-w-full">
          {!isMobile && lenderResponse.benefits.length > 0 && (
            <div className="tw-m-4 tw-rounded-md tw-border tw-border-solid tw-border-primary-150 tw-p-3 xl:tw-mr-2">
              <BidBenefits benefits={lenderResponse.benefits} />
            </div>
          )}

          {collateralFieldRequests.length > 0 && (
            <div className="tw-m-4 tw-rounded-md tw-border tw-border-solid tw-border-primary-150 tw-p-3 xl:tw-mr-2">
              <>
                <DetailsLabel>
                  {t("bidDetails.importantCollateral")}
                </DetailsLabel>

                <LenderResponseFieldRequestDetails
                  fieldRequests={collateralFieldRequests}
                  lenderResponse={lenderResponse}
                />
              </>
            </div>
          )}

          {additionalInformationFieldRequests.length > 0 && (
            <div className="tw-m-4 tw-rounded-md tw-border tw-border-solid tw-border-primary-150 tw-p-3 xl:tw-mr-2">
              <DetailsLabel>
                {t("bidDetails.additionalInformation")}
              </DetailsLabel>

              <LenderResponseFieldRequestDetails
                fieldRequests={additionalInformationFieldRequests}
                lenderResponse={lenderResponse}
              />
            </div>
          )}
        </div>
        <div className="tw-w-full">
          <div className="tw-m-4 tw-rounded-md tw-border tw-border-solid tw-border-primary-150 tw-p-3 xl:tw-ml-2">
            {details}
          </div>
        </div>
      </div>

      <AccordionItem
        title={t("bidDetails.moreAbout") + (lenderCompany.name ?? "")}
        titleStyling="tw-text-sm tw-mb-0 tw-font-bold"
        className={"tw-m-4 tw-mt-0 tw-rounded-md tw-bg-white tw-p-0"}
        id={"0"}
      >
        <Text
          size="sm"
          dangerouslySetInnerHTML={{ __html: lenderCompany.description ?? "" }}
        />
      </AccordionItem>
    </div>

    // <div className={twMerge('overflow-hidden w-100', className)}>
    //   {lenderResponse?.benefits
    //     ?.map((it) => it.type)
    //     .includes(IMPORTANT_INFO_TYPE.EU_LOAN) && (
    //     <div className={twMerge(styles.detailsWrapper, 'px-4 pb-6 p-xl-4')}>
    //       <div className="row">
    //         <div className="col-12 col-lg">
    //           <h3>
    //             <SVG
    //               style={{ marginTop: -4, marginRight: 10 }}
    //               height={30}
    //               width={30}
    //               src={ImportantEuLoanIcon}
    //             />
    //             {t('dash.importantInfo.euLoanBidDetailsHeading')}
    //           </h3>
    //           <p>{t('dash.importantInfo.euLoanBidDetailsText')}</p>
    //         </div>
    //       </div>
    //     </div>
    //   )}
  );
};

export default BidsListItemDetails;
